import {UserRepository} from '../repository/UserRepository';
import {UpdateUserPasswordRequest} from '../models/user.model';
import {AppError, ErrorType} from '../../../Data/config/AppError';

export class UpdateUserPasswordUseCase {
  private readonly userRepository: UserRepository;

  constructor (userRepository: UserRepository) {
    this.userRepository = userRepository;
  }

  async execute (passwordData: UpdateUserPasswordRequest): Promise<void> {
    // Validate required fields
    if (!passwordData.id || passwordData.id < 1) {
      throw new AppError(
        'Valid user ID is required',
        ErrorType.VALIDATION,
        {value: passwordData.id, field: 'id'}
      );
    }

    if (!passwordData.newPassword || passwordData.newPassword.trim() === '') {
      throw new AppError(
        'New password is required',
        ErrorType.VALIDATION,
        {field: 'newPassword'}
      );
    }

    // Validate password strength
    if (passwordData.newPassword.length < 8) {
      throw new AppError(
        'New password must be at least 8 characters long',
        ErrorType.VALIDATION,
        {value: passwordData.newPassword.length, field: 'newPassword'}
      );
    }

    // Validate current password if provided (for non-admin password changes)
    if (passwordData.currentPassword && passwordData.currentPassword.trim() === '') {
      throw new AppError(
        'Current password is required when changing password',
        ErrorType.VALIDATION,
        {field: 'currentPassword'}
      );
    }

    // Validate password confirmation if provided
    if (passwordData.confirmPassword && passwordData.confirmPassword !== passwordData.newPassword) {
      throw new AppError(
        'Password confirmation does not match new password',
        ErrorType.VALIDATION,
        {field: 'confirmPassword'}
      );
    }

    try {
      // Update the user password
      await this.userRepository.updateUserPassword(passwordData);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }

      // Handle specific API errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('user not found')) {
        throw new AppError(
          'User not found',
          ErrorType.NOT_FOUND,
          {value: passwordData.id, field: 'userId'}
        );
      }

      if (errorMessage.includes('current password is incorrect')) {
        throw new AppError(
          'Current password is incorrect',
          ErrorType.VALIDATION,
          {field: 'currentPassword'}
        );
      }

      if (errorMessage.includes('password too weak')) {
        throw new AppError(
          'Password does not meet security requirements',
          ErrorType.VALIDATION,
          {field: 'newPassword'}
        );
      }

      if (errorMessage.includes('password recently used')) {
        throw new AppError(
          'Cannot use a recently used password',
          ErrorType.VALIDATION,
          {field: 'newPassword'}
        );
      }

      if (errorMessage.includes('insufficient permissions')) {
        throw new AppError(
          'Insufficient permissions to change password',
          ErrorType.AUTHORIZATION,
          {value: passwordData.id, field: 'userId'}
        );
      }

      throw new AppError(
        'Failed to update password',
        ErrorType.API_ERROR,
        undefined,
        undefined,
        error
      );
    }
  }
}
